import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Card, CardBody, Col, Spinner } from "reactstrap";
import { getAssetTotalsByCategory } from "../../services/ValitanApiService";

const getCategoryRows = (response) => {
  const payload = response?.data?.data || response?.data || {};
  return Array.isArray(payload?.categories) ? payload.categories : [];
};

const getCategoryCode = (row) =>
  String(row?.category_code || row?.categoryCode || row?.code || "UNKNOWN").toUpperCase();

const getCategoryName = (row) =>
  row?.category_name || row?.categoryName || getCategoryCode(row);

const getAssetRecordCount = (row) =>
  Number(row?.asset_records ?? row?.assetRecords ?? 0);

const getPercentage = (value, total) => {
  if (!total) return 0;
  return Number(((value / total) * 100).toFixed(1));
};

const CategoryChartCard = () => {
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [reviewSeries, setReviewSeries] = useState([0, 0]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviewBreakdown = async () => {
      try {
        setLoading(true);

        const [reviewedResponse, notReviewedResponse] = await Promise.all([
          getAssetTotalsByCategory(true),
          getAssetTotalsByCategory(false),
        ]);

        const reviewedRows = getCategoryRows(reviewedResponse);
        const notReviewedRows = getCategoryRows(notReviewedResponse);
        const grouped = {};

        reviewedRows.forEach((row) => {
          const code = getCategoryCode(row);
          grouped[code] = {
            code,
            name: getCategoryName(row),
            reviewed: getAssetRecordCount(row),
            notReviewed: grouped[code]?.notReviewed || 0,
          };
        });

        notReviewedRows.forEach((row) => {
          const code = getCategoryCode(row);
          grouped[code] = {
            code,
            name: getCategoryName(row),
            reviewed: grouped[code]?.reviewed || 0,
            notReviewed: getAssetRecordCount(row),
          };
        });

        const rows = Object.values(grouped)
          .map((item) => ({
            ...item,
            total: item.reviewed + item.notReviewed,
          }))
          .sort((a, b) => b.total - a.total);

        const reviewedTotal = rows.reduce((sum, row) => sum + row.reviewed, 0);
        const notReviewedTotal = rows.reduce((sum, row) => sum + row.notReviewed, 0);

        setCategoryBreakdown(rows);
        setReviewSeries([reviewedTotal, notReviewedTotal]);
      } catch (error) {
        console.error("Asset review chart fetch error:", error);
        setCategoryBreakdown([]);
        setReviewSeries([0, 0]);
      } finally {
        setLoading(false);
      }
    };

    fetchReviewBreakdown();
  }, []);

  const chartOptions = {
    color: ["#0147c8", "#f1c75b"],
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
      },
      formatter: (params) => {
        const category = categoryBreakdown[params?.[0]?.dataIndex];
        if (!category) return "";

        return `
          <div style="min-width: 180px;">
            <strong>${category.name}</strong><br/>
            Reviewed: ${category.reviewed} (${getPercentage(category.reviewed, category.total)}%)<br/>
            Not Reviewed: ${category.notReviewed} (${getPercentage(category.notReviewed, category.total)}%)<br/>
            Total: ${category.total}
          </div>
        `;
      },
    },
    legend: {
      bottom: 0,
      icon: "roundRect",
      textStyle: {
        color: "#607089",
      },
    },
    grid: {
      left: 0,
      right: 0,
      top: 20,
      bottom: 40,
      containLabel: true,
    },
    xAxis: {
      type: "value",
      splitLine: {
        lineStyle: {
          color: "rgba(6, 37, 90, 0.08)",
        },
      },
      axisLabel: {
        color: "#72819a",
      },
    },
    yAxis: {
      type: "category",
      data: categoryBreakdown.map((category) => category.name),
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        color: "#112240",
        fontWeight: 600,
      },
    },
    series: [
      {
        name: "Reviewed",
        type: "bar",
        stack: "assets",
        barWidth: 18,
        borderRadius: [10, 0, 0, 10],
        data: categoryBreakdown.map((category) => category.reviewed),
      },
      {
        name: "Not Reviewed",
        type: "bar",
        stack: "assets",
        barWidth: 18,
        borderRadius: [0, 10, 10, 0],
        data: categoryBreakdown.map((category) => category.notReviewed),
      },
    ],
  };

  const totalAssets = reviewSeries.reduce((sum, value) => sum + value, 0);
  const overallReviewedPercentage = getPercentage(reviewSeries[0], totalAssets);
  const overallNotReviewedPercentage = getPercentage(reviewSeries[1], totalAssets);

  return (
    <Col xl={4}>
      <Card className="dashboard-widget">
        <CardBody>
          <div className="widget-header">
            <div>
              <h5 className="widget-title">Asset Review by Category</h5>
              <p className="widget-copy">
                Reviewed and not-reviewed asset totals across available categories.
              </p>
            </div>
            <div className="widget-chip">
              {totalAssets} assets
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <ReactECharts
                option={chartOptions}
                style={{ height: 360 }}
              />

              <div className="d-flex flex-wrap gap-2 mt-3">
                <span className="badge rounded-pill text-bg-primary">
                  Reviewed: {reviewSeries[0]} ({overallReviewedPercentage}%)
                </span>
                <span className="badge rounded-pill text-dark" style={{ backgroundColor: "#f5deb0" }}>
                  Not reviewed: {reviewSeries[1]} ({overallNotReviewedPercentage}%)
                </span>
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default CategoryChartCard;
